import { action, computed, observable } from 'mobx';
import {
    addToMap,
    bind,
    ConnectionManager,
    HttpClient,
    humanizeString,
    IAccessTokenStore,
    IConfig,
    ITimezoneStore,
} from 'Mri.Web.Common';
import { ActiveDisplay } from './ActiveDisplay';

export class DisplaysStore implements ITimezoneStore {
    public constructor(httpClient: HttpClient, accessTokenStore: IAccessTokenStore) {
        this.httpClient = httpClient;
        this.connectionManager = new ConnectionManager('/sockets/display', accessTokenStore);
        const { connection } = this.connectionManager;
        connection.on('OnActiveDisplay', this.onActiveDisplay);
        connection.on('OnActiveDisplayStatus', this.onActiveDisplayStatus);
        connection.on('OnTelemetryPoll', this.onTelemetryPoll);
        connection.on('OnSoftwareVersionPoll', this.onSoftwareVersionPoll);
        connection.on('OnCurrentDisplayEvents', this.onCurrentDisplayEvents);
    }

    @action
    public async loadAsync(serialNumber: string, config?: IConfig) {
        const hasStatus = this.displaysWithStatus.has(serialNumber);
        const getStatus = config?.params?.status;

        if (hasStatus && !getStatus) {
            const statusConfig = {
                params: {
                    status: true,
                },
            };

            config = { ...config, ...statusConfig };
        }

        const display = await this.httpClient.get<Mri.Web.Portal.Controllers.Displays.ActiveDisplayModel>(
            `/api/v1/displays/${serialNumber}`,
            config
        );

        if (display && !hasStatus && getStatus) {
            this.displaysWithStatus.set(serialNumber, true);
        }

        if (display) this.displays.set(serialNumber, new ActiveDisplay(display));
    }

    @action
    public async loadWhereSearchAsync(
        model: Mri.Web.Portal.Controllers.Displays.SearchModel,
        config?: IConfig
    ): Promise<Mri.WebApi.PagingResult<string> | undefined> {
        const result = await this.httpClient.post<
        Mri.WebApi.PagingResult<Mri.Web.Portal.Controllers.Displays.ActiveDisplayModel>
        >('/api/v1/displays/search', model, config);

        if (result) {
            const models = result.result;
            const serialNumbers = result.result.map(d => d.serialNumber);

            addToMap(
                this.displays,
                models,
                d => d.serialNumber,
                d => new ActiveDisplay(d)
            );

            if (model.status) models.forEach(d => this.displaysWithStatus.set(d.serialNumber, true));

            return {
                limit: result.limit,
                offset: result.offset,
                result: serialNumbers,
                returned: result.returned,
                total: result.total,
            };
        }
    }

    @action
    public async loadCurrentStatusAsync(serialNumber: string) {
        const status = await this.httpClient.get<Mri.DisplayStatuses.ActiveDisplayStatus>(
            `/api/v1/displays/${serialNumber}/status/current`
        );
        if (status) this.statuses.set(serialNumber, status);
    }

    @action
    public async loadOpenTicketsAsync(serialNumber: string) {
        const tickets = await this.httpClient.get<any[]>(`/api/v1/displays/${serialNumber}/tickets`);

        if (tickets) {
            for (const ticket of tickets) {
                if(typeof ticket.tags === 'string') {
                    ticket.tags = ticket.tags.split(',');
                }
                
                ticket.tags = ticket.tags.map((t: string) => (t = humanizeString(t)));
            }

            this.openTickets.set(serialNumber, tickets);
        }
    }

    @action
    public async loadLatestScreenSnapshotAsync(serialNumber: string) {
        const screenSnapshots = await this.httpClient.get<Mri.Web.Portal.Controllers.Displays.ScreenSnapshotModel[]>(
            `/api/v1/displays/${serialNumber}/screen-snapshot/latest`
        );
        if (screenSnapshots) {
            this.screenSnapshots.set(serialNumber, screenSnapshots);
        }
    }

    @action
    public async loadCurrentScreenSnapshotAsync(serialNumber: string) {
        const screenSnapshots = await this.httpClient.get<Mri.Web.Portal.Controllers.Displays.ScreenSnapshotModel[]>(
            `/api/v1/displays/${serialNumber}/screen-snapshot/current`
        );
        if (screenSnapshots) {
            this.screenSnapshots.set(serialNumber, screenSnapshots);
        }
    }

    @action
    public async updateAsync(serialNumber: string, model: Mri.Web.Portal.Controllers.Displays.UpdateDisplayModel) {
        const display = await this.httpClient.put<Mri.Web.Portal.Controllers.Displays.ActiveDisplayModel>(
            `/api/v1/displays/${serialNumber}`,
            model
        );

        if (display) {
            if (!this.displaysWithStatus.has(serialNumber)) {
                this.displays.set(serialNumber, new ActiveDisplay(display));
            } else {
                await this.loadAsync(serialNumber);
            }
        }
    }
    
    public async getEventHistoryAsync(serialNumber: string, start: Date, end: Date) {
        return this.httpClient.get<Mri.DisplayEvents.DisplayEvent[]>(
            `/api/v1/displays/${serialNumber}/display-events/log`,
            {
                params: {
                    endTime: end,
                    startTime: start,
                },
            }
        );
    }

    @action
    public async loadCurrentDisplayEventsAsync(serialNumber: string) {
        let events = await this.httpClient.get<Mri.Web.Portal.Controllers.Displays.DisplayEventModel[]>(
            `/api/v1/displays/${serialNumber}/display-events/current`
        );

        events = events?.filter(e => e.details != 'WebApi Kicked');

        if (events) this.currentEvents.set(serialNumber, events);
    }

    public async getMinMaxLogAsync(serialNumber: string, start: Date, end: Date) {
        return this.httpClient.get<Mri.Web.Portal.Controllers.Displays.MinMaxModel[]>(
            `/api/v1/displays/${serialNumber}/min-max`,
            {
                params: {
                    endTime: end,
                    startTime: start,
                },
            }
        );
    }

    @action
    public async addFavoriteDisplay(serialNumber: string) {
        const display = await this.httpClient.post<Mri.Web.Portal.Controllers.Displays.ActiveDisplayModel>(
            `/api/v1/displays/${serialNumber}/favorite`
        );

        if (display) {
            if (!this.displaysWithStatus.has(serialNumber)) {
                this.displays.set(serialNumber, new ActiveDisplay(display));
            } else {
                await this.loadAsync(serialNumber);
            }
        }
    }

    @action
    public async removeFavoriteDisplay(serialNumber: string) {
        const display = await this.httpClient.delete<Mri.Web.Portal.Controllers.Displays.ActiveDisplayModel>(
            `/api/v1/displays/${serialNumber}/favorite`
        );

        if (display) {
            if (!this.displaysWithStatus.has(serialNumber)) {
                this.displays.set(serialNumber, new ActiveDisplay(display));
            } else {
                await this.loadAsync(serialNumber);
            }
        }
    }

    @action
    public async loadGpsDataAsync(model: Mri.Web.Portal.Controllers.Displays.GpsSearchModel, config?: IConfig) {
        const gpsData = await this.httpClient.post<Mri.Web.Portal.Controllers.Displays.DisplayGpsModel[]>(
            `/api/v1/displays/gps-data`,
            model,
            config
        );
        if (gpsData) {
            this.gpsData = gpsData;
        }
    }

    @action
    public async subscribeToDisplayAsync(serialNumber: string) {
        console.debug('SubscribeToDisplay', serialNumber);
        await this.connectionManager.connection.send('SubscribeToDisplay', serialNumber);
    }

    @action
    public async unsubscribeToDisplayAsync(serialNumber: string) {
        console.debug('UnsubscribeToDisplay', serialNumber);
        await this.connectionManager.connection.send('UnsubscribeToDisplay', serialNumber);
    }

    public async refresh(serialNumber: string) {
        console.debug('FullRefresh', serialNumber);
        await this.connectionManager.connection.send('FullRefresh', serialNumber);
    }

    @action
    public async refreshScreenSnapshotAsync(serialNumber: string) {
        console.debug('RefreshScreenSnapshot', serialNumber);
        await this.connectionManager.connection.send('RefreshScreenSnapshot', serialNumber);

        const loadingState: { loading: boolean; timeoutHandle: number | null } = observable({
            loading: true,
            timeoutHandle: window.setTimeout(
                action(() => (loadingState.loading = false)),
                10000
            ),
        });
        this.loadingUpdatedSnapshotState.set(serialNumber, loadingState);
    }

    @bind
    @action
    private onScreenSnapshot(models: Mri.Web.Portal.Controllers.Displays.ScreenSnapshotModel[]) {
        try {
            console.debug('onScreenSnapshot', models);
            this.screenSnapshots.set(models[0].displaySerialNumber, models);

            const loadingState = this.loadingUpdatedSnapshotState.get(models[0].displaySerialNumber);
            if (loadingState) {
                loadingState.loading = false;

                if (loadingState.timeoutHandle) {
                    window.clearTimeout(loadingState.timeoutHandle);
                    loadingState.timeoutHandle = null;
                }
            }
        } catch (e: any) {
            console.error(e);
        }
    }

    @bind
    @action
    private onClose(e: Error | undefined) {
        if (e) {
            console.error('onClose', e);
        } else {
            console.debug('onClose disconnected');
        }
    }

    @bind
    @action
    private onActiveDisplay(model: Mri.Web.Portal.Controllers.Displays.ActiveDisplayModel) {
        console.debug('onActiveDisplay', model);

        // reuse aggregates from existing active display
        const currentDisplay = this.displays.get(model.serialNumber);
        const newDisplay = new ActiveDisplay(model);
        newDisplay.aggregates = currentDisplay?.aggregates;

        this.displays.set(model.serialNumber, newDisplay);
    }

    @bind
    @action
    private onActiveDisplayStatus(model: Mri.DisplayStatuses.ActiveDisplayStatus) {
        console.debug('onActiveDisplayStatus', model);
        this.statuses.set(model.displaySerialNumber, model);
    }

    @bind
    private onTelemetryPoll(model: Mri.TelemetryPolls.TelemetryPoll) {
        console.debug('onTelemetryPoll (empty)', model);
    }

    @bind
    @action
    private onSoftwareVersionPoll(model: Mri.DisplaySoftware.SoftwareVersionPoll) {
        console.debug('onSoftwareVersionPoll', model);
        this.softwareVersions.set(model.displaySerialNumber, model.softwareVersions);
    }

    @bind
    @action
    private onCurrentDisplayEvents(
        serialNumber: string,
        model: Mri.Web.Portal.Controllers.Displays.DisplayEventModel[]
    ) {
        console.debug('onCurrentDisplayEvents', model);
        this.currentEvents.set(serialNumber, model);
    }

    @computed
    public get timezones(): Map<string, string | undefined> {
        return new Map(Array.from(this.displays, ([k, v]) => [k, v.timezone]));
    }

    @observable
    public displays = new Map<string, ActiveDisplay>();
    @observable
    public displaysWithStatus = new Map<string, boolean>();
    @observable
    public statuses = new Map<string, Mri.DisplayStatuses.ActiveDisplayStatus>();
    @observable
    public currentEvents = new Map<string, Mri.Web.Portal.Controllers.Displays.DisplayEventModel[]>();
    @observable
    public softwareVersions = new Map<string, Mri.DisplaySoftware.SoftwareVersion[]>();
    @observable
    public loadingUpdatedSnapshotState = new Map<string, { loading: boolean; timeoutHandle: number | null }>();
    @observable
    public openTickets = new Map<string, any[]>();
    @observable
    public screenSnapshots = new Map<string, Mri.Web.Portal.Controllers.Displays.ScreenSnapshotModel[]>();
    @observable
    public displaySnapshotRefreshStatus = new Map<string, boolean>();
    @observable
    public gpsData: Mri.Web.Portal.Controllers.Displays.DisplayGpsModel[] = [];
    @observable
    public screenOrientations = new Map<string, boolean>();
    public readonly connectionManager: ConnectionManager;
    private readonly httpClient: HttpClient;
}
